import { motion } from 'framer-motion';
import {
  Search, SlidersHorizontal, Plus, MoreVertical
} from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';

const statusTabs = [
  { label: 'All', count: 42, active: true },
  { label: 'Active', count: 28 },
  { label: 'Inactive', count: 6 },
  { label: 'Pending', count: 8 },
];

// Only Burjeel facility users
const users = [
  { name: 'Ahmed Ali',        facility: 'Burjeel Al Nahyan',    others: 1, status: 'Active',   dateAdded: "15 Nov' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "17 Nov' 25" },
  { name: 'Fatima Al Ali',    facility: 'Burjeel - Mussafah',   others: 2, status: 'Active',   dateAdded: "5 Nov' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Nov' 25" },
  { name: 'Aisha Al Mansoori',facility: 'Burjeel Royal',        others: 0, status: 'Inactive', dateAdded: "26 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "30 Oct' 25" },
  { name: 'Khalid Al Ameri',  facility: 'Burjeel Al Nahyan',    others: 1, status: 'Pending',  dateAdded: "15 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "19 Oct' 25" },
  { name: 'Sara Al Mulla',    facility: 'Burjeel - Mussafah',   others: 2, status: 'Active',   dateAdded: "10 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "12 Oct' 25" },
  { name: 'Nasser Al Neyadi', facility: 'Burjeel Day Surgery',  others: 0, status: 'Active',   dateAdded: "2 Oct' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Oct' 25" },
  { name: 'Amna Al Mazrouei', facility: 'Burjeel Royal',        others: 1, status: 'Pending',  dateAdded: "28 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "1 Oct' 25" },
  { name: 'Tariq Al Mutawa',  facility: 'Burjeel Al Nahyan',    others: 1, status: 'Active',   dateAdded: "21 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "15 Oct' 25" },
];

function StatusBadge({ status }) {
  const styles = {
    Active: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    Inactive: 'bg-gray-50 text-gray-500 border-gray-200',
    Pending: 'bg-amber-50 text-amber-600 border-amber-200',
  };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>{status}</span>;
}

export default function PortalDashboardScene({ onNext }) {
  // Flow:
  //  t=0.4s  cursor enters, hovers near title InfoSpot
  //  t=1.0s  InfoSpot (title) auto-opens
  //  t=3.5s  InfoSpot closes, cursor moves toward Add new user button
  //  t=4.0s  CTA pulses (zoom in/out) to attract click
  //  t=5.5s  cursor clicks Add new user
  const cursorPath = [
    { x: 130, y: 55,  delay: 0.8 },   // hover over Users title InfoSpot
    { x: 130, y: 55,  delay: 2.5 },   // linger while tooltip is open
    { x: 1040, y: 60, delay: 1.8 },   // move to Add new user
    { x: 1040, y: 60, delay: 1.5, click: true }, // click
  ];

  return (
    <PortalShell
      activeItem="User"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.4} />}
    >
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="px-8 pt-5 pb-3">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl text-[#0A0A0A] flex items-center gap-2">
              Users
              <InfoSpot
                text="The Users page shows all users across your assigned facilities. Admins can manage roles, view activity, and add new users."
                position="bottom"
                autoOpenAt={1.0}
                autoCloseAfter={2.2}
                glowAt={0.7}
              />
            </h1>
            <p className="font-body text-sm text-[#5F6368] mt-1">Manage the users and their roles & permissions at Burjeel</p>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              data-testid="add-new-user-button"
              onClick={onNext}
              animate={{
                scale:     [1, 1,    1.12, 0.96, 1.08, 1,    1,    1.05, 0.98, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.35)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 6px rgba(13,148,136,0.25)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 3.8, delay: 3.6, times: [0, 0.3, 0.38, 0.46, 0.55, 0.65, 0.75, 0.82, 0.9, 1] }}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-body font-medium hover:bg-teal-700 transition-colors cursor-pointer"
            >
              <Plus size={16} /> Add new user
            </motion.button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            {statusTabs.map((tab, i) => (
              <motion.button key={tab.label} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.06 }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-body ${tab.active ? 'bg-[#0A0A0A] text-white' : 'bg-[#F5F5F5] text-[#5F6368]'}`}>
                {tab.label} <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab.active ? 'bg-white/20' : 'bg-[#E5E7EB]'}`}>{tab.count}</span>
              </motion.button>
            ))}
          </div>
          <div className="flex items-center gap-2"><Search size={16} className="text-[#5F6368]" /><SlidersHorizontal size={16} className="text-[#5F6368]" /></div>
        </div>
      </motion.div>

      <div className="px-8">
        <table className="w-full">
          <thead><tr className="border-b border-[#E5E7EB]">
            {['User name', 'Facility', 'Status', 'Date added', 'Last login', 'Last modified', ''].map(h => (
              <th key={h} className="text-left py-2.5 text-[12px] font-body font-medium text-[#5F6368]">{h}</th>
            ))}
          </tr></thead>
          <tbody>{users.map((u, i) => (
            <motion.tr key={u.name} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.04 }} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]/50">
              <td className="py-3 text-[13px] font-body font-medium text-[#0A0A0A]">{u.name}</td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">
                {u.facility}
                {u.others > 0 && <span className="text-teal-600 text-[11px] ml-1">+{u.others} others</span>}
              </td>
              <td className="py-3"><StatusBadge status={u.status} /></td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">{u.dateAdded}</td>
              <td className="py-3"><p className="text-[13px] font-body text-[#5F6368]">{u.lastLogin}</p><p className="text-[11px] font-body text-[#5F6368]/60">{u.lastLoginTime}</p></td>
              <td className="py-3 text-[13px] font-body text-[#5F6368]">{u.lastModified}</td>
              <td className="py-3"><MoreVertical size={14} className="text-[#5F6368]" /></td>
            </motion.tr>
          ))}</tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-8 py-3 border-t border-[#E5E7EB]">
        <span className="text-[12px] font-body text-[#5F6368]">Page 1 of 3</span>
        <div className="flex items-center gap-1">
          <button className="px-2.5 py-1 text-[12px] font-body text-[#5F6368]">&lt; Previous</button>
          <button className="w-7 h-7 rounded-md bg-[#0A0A0A] text-white text-[12px] font-body font-medium">1</button>
          <button className="w-7 h-7 rounded-md text-[12px] font-body text-[#5F6368]">2</button>
          <button className="w-7 h-7 rounded-md text-[12px] font-body text-[#5F6368]">3</button>
          <button className="px-2.5 py-1 text-[12px] font-body text-[#5F6368]">Next &gt;</button>
        </div>
      </div>
    </PortalShell>
  );
}
